'use client';

import { Lock, KeyRound, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BoardroomIcon } from '@/components/shared';

/**
 * Chat gate banner — three render modes, picked by the host (chat-
 * panel.tsx) based on the verify query state:
 *
 *   1. `mode === 'connect'` — no wallet connected. CTA opens Privy
 *      login modal.
 *   2. `mode === 'sign-in-required'` — wallet connected (Privy
 *      session active) but the wagmi-active address isn't part of
 *      that session, so we couldn't run the seat check. CTA fires
 *      `wallet.loginOrLink()` for one SIWE signature; on success
 *      the verify refetches and we drop into either `chat` or
 *      `seat-required`. Optional `errorMessage` slot surfaces
 *      Privy errors (e.g. "wallet linked to another account").
 *   3. `mode === 'seat-required'` — wallet connected, fully linked,
 *      seat check returned `verified: false`. No CTA — the user
 *      needs to acquire a seat (or switch back to a wallet that
 *      holds one).
 *
 * The legacy boolean props (`isConnected`, `isHolder`) are kept for
 * call sites that haven't migrated; they map onto `mode` via
 * `deriveModeFromLegacyProps`. New call sites should pass `mode`.
 */
type GateMode = 'connect' | 'sign-in-required' | 'seat-required';

interface ChatGateBannerProps {
  /** Preferred way to drive the banner. If set, takes precedence over
   *  the `isConnected` / `isHolder` legacy fields. */
  mode?: GateMode;
  /** Legacy: only consulted when `mode` is omitted. */
  isConnected?: boolean;
  /** Legacy: only consulted when `mode` is omitted. */
  isHolder?: boolean | null;
  /** Click handler for the CTA. Required when `mode` is `connect` or
   *  `sign-in-required`; ignored for `seat-required`. */
  onConnect?: () => void;
  /** True while the sign-in CTA is in flight (a SIWE prompt is sitting
   *  in the user's wallet). Only meaningful for `sign-in-required`;
   *  swaps the button label/icon for a loader. */
  isSigningIn?: boolean;
}

function deriveModeFromLegacyProps(
  isConnected: boolean | undefined,
  isHolder: boolean | null | undefined,
): GateMode {
  if (!isConnected) return 'connect';
  if (isHolder === false) return 'seat-required';
  return 'connect';
}

export function ChatGateBanner({
  mode,
  isConnected,
  isHolder,
  onConnect,
  isSigningIn = false,
}: ChatGateBannerProps) {
  const resolvedMode = mode ?? deriveModeFromLegacyProps(isConnected, isHolder);

  const icon =
    resolvedMode === 'connect' ? (
      <BoardroomIcon size="lg" />
    ) : resolvedMode === 'sign-in-required' ? (
      <KeyRound className="h-6 w-6 text-amber-200/70" />
    ) : (
      <Lock className="h-6 w-6 text-amber-200/70" />
    );

  const title =
    resolvedMode === 'connect'
      ? 'Chat for seat holders'
      : resolvedMode === 'sign-in-required'
        ? 'Sign in to chat'
        : 'Seat required';

  const body =
    resolvedMode === 'connect'
      ? 'Connect a wallet that holds a seat to join the room.'
      : resolvedMode === 'sign-in-required'
        ? 'We need to verify this wallet before checking seat ownership.'
        : 'Hold a seat on this board to participate in chat.';

  const ctaLabel =
    resolvedMode === 'connect'
      ? 'Connect wallet'
      : resolvedMode === 'sign-in-required'
        ? 'Sign in'
        : null;

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-12 px-6 text-center">
      <div
        className={cn(
          'flex items-center justify-center w-14 h-14 rounded-[4px]',
          'bg-black/50 border border-amber-500/25 shadow-[inset_0_0_0_1px_rgba(255,215,120,0.08)]',
        )}
      >
        {icon}
      </div>

      <div className="space-y-1.5">
        <p className="text-sm font-medium text-amber-100">{title}</p>
        <p className="text-xs text-zinc-400 max-w-[260px] leading-relaxed">{body}</p>
      </div>

      {ctaLabel && (
        <button
          type="button"
          onClick={onConnect}
          disabled={isSigningIn}
          className={cn(
            'mt-1 px-4 h-8 rounded-[4px] text-[10px] font-mono uppercase tracking-[0.15em]',
            'bg-gradient-to-b from-amber-500/20 to-amber-500/5 border border-amber-400/45',
            'text-amber-100 hover:border-amber-300/70 hover:from-amber-500/30 transition-colors',
            'disabled:opacity-60 disabled:cursor-not-allowed',
            'inline-flex items-center gap-2',
          )}
        >
          {isSigningIn && <Loader2 className="h-3 w-3 animate-spin" />}
          {isSigningIn ? 'Signing in…' : ctaLabel}
        </button>
      )}
    </div>
  );
}
